import polly from 'polly-js';
import _ from "lodash";
import { ApiClient } from '../api-client';
import { useLocal } from './../environment';
import { protectedResources } from '../../authConfig';

export class CompanyAPI {
    static companies = [];

    constructor(instance, inProgress, accounts, useCache = true) {
        this.instance = instance;
        this.inProgress = inProgress;
        this.accounts = accounts;
        this.useCache = useCache;
        this.apiClient = new ApiClient(instance, inProgress, accounts);
        // Use centrally defined scopes for the Property Manager API,
        // consistent with other API clients.
        this.readScopes = [
            ...protectedResources.apiCompany.scopes.read
        ];
        this.writeScopes = [
            ...protectedResources.apiCompany.scopes.readWrite
        ];

        this.baseUrl = useLocal ?
            "http://localhost:8082/api/companies" :
            "https://apim.ianrobertson.co.uk/api/companies/v1";
    }


    #getCompaniesFromCache = () => {
        if (!this.useCache) return [];

        return CompanyAPI.companies;
    };

    #setCompaniesToCache = (companies) => {
        if (!this.useCache) return;

        CompanyAPI.companies = companies;
    };

    static clearCache = () => {
        CompanyAPI.companies = [];
    };

    #addCompanyToCache = company => {
        if (!this.useCache) return;

        const companies = this.#getCompaniesFromCache();
        const existingCompany = _.find(companies, c => c.id === company.id);
        
        if (existingCompany) {
            Object.assign(existingCompany, company);
        } else {
            companies.push(company);
        }
        
        this.#setCompaniesToCache(companies);
    };

    getAll = async () => {
        return polly()
            .logger(error => {
                console.error("An error occurred in getAll", error);
            })
            .waitAndRetry(5)
            .executeForPromise(async () => {
                const cachedCompanies = this.#getCompaniesFromCache();
                if (this.useCache && cachedCompanies.length > 0) {
                    return cachedCompanies;
                }

                try {
                    const result = await this.apiClient.get(`${this.baseUrl}/GetAll`, this.readScopes);
                    this.#setCompaniesToCache(result);
                    return result;
                } catch (error) {
                    this.apiClient.handleAuthError(error);
                    console.log(error);
                    throw error;
                }
            });
    };

    get = async id => {
        return polly()
            .logger(error => {
                console.error("An error occurred in get", error);
            })
            .waitAndRetry(5)
            .executeForPromise(async () => {
                // Check cache first
                const companies = this.#getCompaniesFromCache();
                const company = _.find(companies, c => c.id === id);
                if (company) {
                    return company; // found in cache
                }

                try {
                    const result = await this.apiClient.get(`${this.baseUrl}/${id}`, this.readScopes);
                    this.#addCompanyToCache(result);
                    return result;
                } catch (error) {
                    this.apiClient.handleAuthError(error);
                    console.log(error);
                    throw error;
                }
            });
    };

    // Add getCompany as an alias for get to support both naming conventions
    getCompany = this.get;

    getLandlord = async id => {
        return polly()
            .logger(error => {
                console.error("An error occurred in getLandlord", error);
            })
            .waitAndRetry(5)
            .executeForPromise(async () => {
                // Check cache first
                const companies = this.#getCompaniesFromCache();
                const landlord = _.find(_.flatMap(companies, c => c.landlordsOrOwners), l => l.id === id);
                if (landlord) {
                    return landlord; // found in cache
                }

                try {
                    const data = await this.getAll();
                    const fetchedLandlord = _.find(_.flatMap(data, c => c.landlordsOrOwners), l => l.id === id);
                    if (fetchedLandlord) {
                        return fetchedLandlord;
                    }
                    throw new Error(`Landlord with ID ${id} not found`);
                } catch (error) {
                    console.log("Error getting landlord.", error);
                    throw error;
                }
            });
    };

    getAllLandlords = async () => {
        return polly()
            .logger(error => {
                console.error("An error occurred in getAllLandlords", error);
            })
            .waitAndRetry(5)
            .executeForPromise(async () => {
                const cachedCompanies = this.#getCompaniesFromCache();
                if (this.useCache && cachedCompanies.length > 0) {
                    const cachedLandlords = _.flatMap(cachedCompanies, c => c.landlordsOrOwners || []);
                    if (cachedLandlords.length > 0) {
                        return cachedLandlords;
                    }
                }

                const companies = await this.getAll();
                const landlords = _.flatMap(companies, c => {
                    const companyParent = _.pick(c, ['id', 'name', 'createdBy', 'created', 'modifiedBy', 'modified']);
                    return (c.landlordsOrOwners || []).map(l => {
                        if (!l.parent) {
                            l.parent = companyParent;
                        }
                        return l;
                    });
                });
                return landlords;
            });
    };

    updateCompany = async (companyDoc, id) => {
        return polly()
            .logger(error => {
                console.error("An error occurred in updateCompany", error);
            })
            .waitAndRetry(5)
            .executeForPromise(async () => {
                try {
                    const result = await this.apiClient.patch(`${this.baseUrl}/${id}`, companyDoc, this.writeScopes);

                    // Update parent references for landlords
                    for (const landlord of result.landlordsOrOwners) {
                        landlord.parent = _.pick(result, ['id', 'name', 'createdBy', 'created', 'modifiedBy', 'modified']);
                    }

                    CompanyAPI.clearCache(); // Invalidate cache after update
                    this.#addCompanyToCache(result);
                    return result;
                } catch (error) {
                    this.apiClient.handleAuthError(error);
                    console.error("An error occurred updating company", error);
                    throw error;
                }
            });
    };

    updateLandlord = async (companyId, landlordData) => {
        const companies = this.#getCompaniesFromCache();
        const company = _.find(companies, c => c.id === companyId) || await this.get(companyId);

        const existingLandlordIndex = _.findIndex(company.landlordsOrOwners, l => l.id === landlordData.id);

        if (existingLandlordIndex === -1) {
            return Promise.reject("Landlord Index not found");
        }

        company.landlordsOrOwners.splice(existingLandlordIndex, 1, landlordData);
        return await this.updateCompany(company, companyId);
    };

    createCompany = async newCompany => {
        // Create a variable to store auth errors that shouldn't be retried
        let authError = null;

        return polly()
            .logger(error => {
                console.error("An error occurred in createCompany", error);
            })
            .handle(err => {
                // If we've already detected an auth error, don't retry
                if (authError) return false;

                // Don't retry for 401/403 errors
                if (err.status === 401 || err.status === 403) {
                    authError = err; // Store the auth error
                    console.log(`Authorization error (${err.status}), not retrying`);
                    return false; // Don't retry
                }

                return true; // Retry for other errors
            })
            .waitAndRetry(5)
            .executeForPromise(async () => {
                // If we already have an auth error from a previous attempt, reject immediately
                if (authError) {
                    throw authError;
                }

                try {
                    const result = await this.apiClient.post(`${this.baseUrl}/Create`, newCompany, this.writeScopes);
                    CompanyAPI.clearCache(); // Invalidate cache after create
                    return result;
                } catch (error) {
                    console.log(error);

                    // If this is an HTTP error we created above, we can access the status
                    if (error.status) {
                        console.log(`API returned status code: ${error.status}`);
                        // Store auth errors so we don't retry
                        if (error.status === 401 || error.status === 403) {
                            authError = error;
                            console.log("Authorization error, not retrying");
                        }
                    }

                    this.apiClient.handleAuthError(error);
                    throw error;
                }
            });
    };
}